import React, { useState } from 'react'
import { Button } from '~/components/common/Button'
import { InputArea } from '~/components/common/InputArea'
import { Typography } from '~/components/common/Typography'
import { CloseIcon, Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import { AccountType } from '~/constants/settings'
import { useOptionalBillingContext } from '~/contexts/BillingContext'
import {
  BookmarkVisibility,
  PromptBookmark,
  useCreatePromptBookmark,
  useDeletePromptBookmark,
  useUpdatePromptBookmark
} from '~/services/promptBookmarkService'
import { BetterToast } from '~/utils/toast'
import { BookmarkIcon, LockIcon, PeopleIcon } from './bookmarkIcons'

// Account types allowed to publish "shared with team" bookmarks. Mirrors
// the backend gate in api/routes/api/billing/prompt_bookmarks.py — keep
// these in sync. Personal bookmarks remain available on every plan.
const TEAM_OR_BETTER: readonly AccountType[] = [
  AccountType.TEAM,
  AccountType.ENTERPRISE,
  AccountType.ENTERPRISE_MIGRATE,
  AccountType.ENTERPRISE_MIGRATE_ASSESS,
  AccountType.ENTERPRISE_MIGRATE_ACCELERATE,
  AccountType.ENTERPRISE_MIGRATE_REFACTOR,
  AccountType.EXEMPT
]

/**
 * Whether the currently-selected billing account is on a plan that can
 * publish shared bookmarks. Falls back to `true` when the BillingContext
 * isn't mounted (isolated tests, embedded previews) so we don't
 * spuriously hide the toggle in surfaces that intentionally don't wire
 * the full provider tree — the backend still enforces the gate.
 */
function useCanShareBookmark(): boolean {
  const billing = useOptionalBillingContext()
  if (!billing) return true
  return TEAM_OR_BETTER.some(t => billing.hasLicense(t))
}

export interface BookmarkEditorModalProps {
  isOpen: boolean
  onClose: () => void
  editing: PromptBookmark | null
  defaultBody: string
}

/**
 * Create / edit / delete a single prompt bookmark.
 *
 * This is the heavyweight "real" bookmark surface — opened from the
 * popover's New / Edit affordances and from the SaveAsBookmarkButton
 * that hover-attaches to past chat messages. Anything that mutates
 * a bookmark goes through this modal.
 */
export function BookmarkEditorModal({
  isOpen,
  onClose,
  editing,
  defaultBody
}: BookmarkEditorModalProps) {
  const createMut = useCreatePromptBookmark()
  const updateMut = useUpdatePromptBookmark()
  const deleteMut = useDeletePromptBookmark()
  const canShare = useCanShareBookmark()

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [visibility, setVisibility] = useState<BookmarkVisibility>('personal')
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset form whenever the modal opens or the editing target changes.
  // Deliberately keyed on isOpen so reopening "create" after a successful
  // save doesn't carry stale fields from the prior session.
  //
  // canShare is intentionally NOT a dep: when billing context resolves
  // async (e.g. license data arrives after the modal opens, or the user
  // switches accounts mid-edit) canShare flips and would otherwise wipe
  // the user's in-progress title/body/visibility. The plan-gate is
  // still enforced — the Share toggle is re-rendered against canShare
  // directly below, and the backend independently 403s a shared write
  // from a plan that doesn't allow it.
  React.useEffect(() => {
    if (!isOpen) return
    setSubmitError(null)
    if (editing) {
      setTitle(editing.title || '')
      setBody(editing.body)
      // If we're opening into a previously-shared bookmark on a plan
      // that no longer allows sharing (e.g. account downgraded after
      // the fact), preserve the existing visibility — the user can
      // explicitly flip it to private from the toggle, and the backend
      // 403s any save that tries to *keep* it shared on this plan.
      setVisibility(editing.visibility)
    } else {
      setTitle('')
      setBody(defaultBody)
      setVisibility('personal')
    }
  }, [isOpen, editing, defaultBody])

  const isBusy = createMut.isPending || updateMut.isPending
  const trimmedBody = body.trim()
  const canSubmit = trimmedBody.length > 0 && !isBusy

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitError(null)
    const trimmedTitle = title.trim()
    const displayTitle = trimmedTitle || 'Untitled'
    // Persist the trimmed body so leading/trailing whitespace doesn't
    // sneak past the canSubmit guard and end up re-inserted into chat
    // (a body of pure whitespace would otherwise round-trip as a blank
    // bookmark and silently insert nothing when picked).
    const payloadBody = trimmedBody
    try {
      if (editing) {
        await updateMut.mutateAsync({
          uuid: editing.uuid,
          payload: { title: trimmedTitle, body: payloadBody, visibility }
        })
        BetterToast.success(`Updated "${displayTitle}"`)
      } else {
        await createMut.mutateAsync({
          title: trimmedTitle,
          body: payloadBody,
          visibility
        })
        BetterToast.success(`Saved "${displayTitle}" to bookmarks`)
      }
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save bookmark'
      setSubmitError(msg)
      BetterToast.error(msg)
    }
  }

  const handleDelete = async () => {
    if (!editing) return
    if (!window.confirm(`Delete bookmark "${editing.title || 'Untitled'}"?`)) return
    try {
      await deleteMut.mutateAsync({ uuid: editing.uuid })
      BetterToast.success(`Deleted "${editing.title || 'Untitled'}"`)
      onClose()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete bookmark'
      BetterToast.error(msg)
    }
  }

  const onTitleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter in the title field jumps focus to the body — feels more like
    // a doc title than a form input, and keeps the keyboard flow snappy.
    if (e.key === 'Enter') {
      e.preventDefault()
      const ta = document.querySelector<HTMLTextAreaElement>('[data-testid="bookmark-body-input"]')
      ta?.focus()
    }
  }

  const onBodyKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd/Ctrl+Enter saves — mirrors the chat-send shortcut so users
    // don't have to mouse over to the Save button mid-flow.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      void handleSubmit()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} hideCloseButton>
      <div
        className='flex items-center justify-between border-b border-tokens-neutral-border-2 py-2 pl-6 select-none'
        data-testid='bookmark-editor-header'
      >
        <div className='flex h-9 min-w-0 flex-1 items-center gap-4'>
          <div className='flex size-8 items-center justify-center rounded-full p-2 text-tokens-neutral-bg-action shrink-0'>
            <BookmarkIcon filled className='size-[18px]' />
          </div>
          <div className='flex min-w-0 flex-col justify-center'>
            <span data-testid='bookmark-editor-header-title'>
              <Typography
                fontFamily='geist'
                fontSize='titleSm'
                fontWeight='bold'
                className='text-tokens-neutral-text-primary'
              >
                {editing ? 'Edit bookmark' : 'New prompt bookmark'}
              </Typography>
            </span>
            <span data-testid='bookmark-editor-header-description'>
              <Typography
                fontFamily='geist'
                fontSize='bodySmMedium'
                fontWeight='medium'
                className='text-tokens-neutral-text-secondary'
              >
                {editing ? 'Update this saved prompt.' : 'Save a prompt to reuse later.'}
              </Typography>
            </span>
          </div>
        </div>
        <button
          type='button'
          onClick={onClose}
          aria-label='Close modal'
          className='flex items-center justify-center p-5 text-tokens-neutral-icon-hover hover:bg-tokens-neutral-fill-fill-6 transition-colors'
        >
          <CloseIcon className='size-6' />
        </button>
      </div>

      <ModalBody className='!px-6 !py-6'>
        <div className='flex flex-col gap-5' data-testid='bookmark-editor-form'>
          <input
            type='text'
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={onTitleKey}
            maxLength={120}
            placeholder='Untitled'
            aria-label='Bookmark title'
            className='h-9 rounded-[12px] bg-tokens-neutral-bg-white border border-tokens-neutral-border-1 px-3 py-2 text-sm font-medium leading-[22px] text-tokens-neutral-text-primary placeholder:text-tokens-neutral-text-placeholder focus:outline-none focus:border-tokens-status-link-2 transition-colors'
            data-testid='bookmark-title-input'
          />

          {/* Body — section-style field with a real heading on top, char
              counter on the right, then the textarea framed as a soft
              recess (subtle inset shadow). */}
          <div className='flex flex-col gap-2'>
            <div className='flex items-center justify-between select-none'>
              <span className='text-sm font-semibold text-tokens-neutral-text-primary'>Prompt</span>
              <span className='text-xs text-tokens-neutral-text-tertiary tabular-nums'>
                {body.length.toLocaleString()} / 16,000
              </span>
            </div>
            <InputArea
              value={body}
              onChange={e => setBody(e.target.value)}
              onKeyDown={onBodyKey}
              size='large'
              fullWidth
              maxLength={16000}
              placeholder='Write the prompt body here…'
              className='h-[64px] min-h-[64px] rounded-[12px] border-tokens-neutral-border-1 bg-tokens-neutral-bg-white px-3 py-2 text-sm font-normal leading-4 tracking-[0.56px] text-tokens-neutral-text-primary placeholder:text-sm placeholder:font-normal placeholder:leading-4 placeholder:tracking-[0.56px] placeholder:text-tokens-neutral-text-placeholder focus:border-tokens-status-link-2 focus:outline-none'
              data-testid='bookmark-body-input'
            />
            <span className='text-xs text-tokens-neutral-text-tertiary select-none'>
              <kbd className='px-1.5 py-0.5 rounded border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white text-[10px] font-mono shadow-segment'>
                {typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform) ? '⌘' : 'Ctrl'}
              </kbd>{' '}
              +{' '}
              <kbd className='px-1.5 py-0.5 rounded border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white text-[10px] font-mono shadow-segment'>
                Enter
              </kbd>{' '}
              to save
            </span>
          </div>

          {/* Sharing — modeled as an action ("Share with your workspace") rather
              than a passive radio. Reads more naturally: bookmarks default
              to private, and you explicitly share them. Toggle is reversible
              from the same row.

              On individual plans the toggle is hidden entirely — a
              disabled control would just be teaser UI. The backend
              independently 403s any attempt to PATCH visibility=account
              from those plans, so this is purely a UX nicety. */}
          {(canShare || visibility === 'account') && (
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-semibold text-tokens-neutral-text-primary select-none'>
                Sharing
              </span>
              <ShareRow visibility={visibility} onChange={setVisibility} canShare={canShare} />
            </div>
          )}

          {submitError && (
            <div
              className='rounded-[12px] border border-tokens-status-error-1 bg-tokens-status-error-text/5 px-3.5 py-2.5 text-sm text-tokens-status-error-text'
              data-testid='bookmark-editor-error'
            >
              {submitError}
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter
        startContent={
          editing ? (
            <Button
              variant='destructive'
              onClick={handleDelete}
              isDisabled={isBusy || deleteMut.isPending}
              isLoading={deleteMut.isPending}
              data-testid='bookmark-editor-delete'
            >
              Delete
            </Button>
          ) : undefined
        }
        endContent={
          <>
            <Button variant='outlined' onClick={onClose} isDisabled={isBusy}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              isDisabled={!canSubmit}
              isLoading={isBusy}
              className={`relative cursor-default ${editing ? 'w-[92px]' : 'w-[168px]'} [&>[aria-hidden=true]]:absolute [&>[aria-hidden=true]]:left-3`}
              data-testid='bookmark-save-button'
            >
              {editing ? 'Save' : 'Create bookmark'}
            </Button>
          </>
        }
      />
    </Modal>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Sharing toggle (private to this modal — not a reusable design system
// component, so it lives next to the only consumer that needs it).
// ──────────────────────────────────────────────────────────────────────

function ShareRow({
  visibility,
  onChange,
  canShare
}: {
  visibility: BookmarkVisibility
  onChange: (v: BookmarkVisibility) => void
  canShare: boolean
}) {
  const isShared = visibility === 'account'
  return (
    <div
      className={`flex items-center justify-between gap-3 px-3.5 py-3 rounded-[12px] border transition-colors select-none ${
        isShared
          ? 'border-tokens-status-link-2 bg-tokens-status-link-base shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]'
          : 'border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1 shadow-[inset_0_1px_2px_rgba(0,0,0,0.03)]'
      }`}
      role='group'
      aria-label='Bookmark sharing'
    >
      <div className='flex items-center gap-2.5 min-w-0'>
        <span
          className={`flex items-center justify-center w-7 h-7 rounded-lg shadow-[inset_0_0_0_1px_rgba(15,15,15,0.06)] ${
            isShared
              ? 'bg-tokens-neutral-bg-white text-tokens-status-link-2'
              : 'bg-tokens-neutral-bg-white text-tokens-neutral-text-secondary'
          }`}
          aria-hidden='true'
        >
          {isShared ? <PeopleIcon /> : <LockIcon />}
        </span>
        <div className='flex flex-col min-w-0'>
          <span className='text-sm font-medium text-tokens-neutral-text-primary leading-tight'>
            {isShared ? 'Shared with this workspace' : 'Private to this workspace'}
          </span>
          <span className='text-xs text-tokens-neutral-text-secondary leading-tight'>
            {isShared
              ? 'Anyone in this workspace can use it.'
              : 'Only you can see this bookmark, and only in this workspace.'}
          </span>
        </div>
      </div>
      {isShared ? (
        <Button
          variant='secondary'
          onClick={() => onChange('personal')}
          data-testid='bookmark-visibility-personal'
          className='h-9 rounded-[12px] px-3 py-2 text-sm shrink-0'
        >
          Make private
        </Button>
      ) : canShare ? (
        <Button
          variant='secondary'
          onClick={() => onChange('account')}
          data-testid='bookmark-visibility-workspace'
          className='h-9 rounded-[12px] px-3 py-2 text-sm shrink-0'
          leftIcon={<PeopleIcon />}
        >
          Share
        </Button>
      ) : null}
    </div>
  )
}
